@let row = item();
@if (row) {
  <div class="soft-product-detail">
    <!-- Header (mirrors the customer info panel header) -->
    <div class="soft-product-detail__header">
      <div class="w-full p-2 pb-0">
        <div class="flex items-start justify-between gap-3 w-full">
          <div class="flex flex-col min-w-0 gap-0.5 p-3">
            @if (isLoadingProduct() && !title()) {
              <soft-ds-loading-skeleton width="90px" height="12px" radius="4px" />
              <soft-ds-loading-skeleton width="220px" height="20px" radius="4px" />
              <soft-ds-loading-skeleton width="160px" height="12px" radius="4px" />
            } @else {
              @if (articleNumber()) {
                <span class="text-light text-sm">Art.-Nr. {{ articleNumber() }}</span>
              }
              <span class="text-lg font-semibold">{{ title() }}</span>
              @for (line of descriptionLines(); track $index) {
                <div class="ds-caption text-light">{{ line }}</div>
              }
            }

            <!-- Status badge row — only claims listed/not-listed once the product
                   actually loaded; loading shows a pill, error shows nothing. -->
            <div class="flex flex-wrap items-center gap-2 mt-1">
              @if (isLoadingProduct()) {
                <soft-ds-loading-skeleton width="5.5rem" height="22px" radius="999px" />
              } @else if (product()) {
                @if (isListed()) {
                  <soft-ds-badge size="md" variant="success">
                    <soft-ds-icon name="fa-regular fa-check" size="xs" />
                    Gelistet
                  </soft-ds-badge>
                } @else {
                  <soft-ds-badge size="md" variant="secondary">
                    <soft-ds-icon name="fa-regular fa-ban" size="xs" />
                    Nicht gelistet
                  </soft-ds-badge>
                }
                @if (isCustomizable()) {
                  <soft-ds-badge size="md" variant="primary">
                    <soft-ds-icon name="fa-regular fa-pen" size="xs" />
                    Manueller Artikel
                  </soft-ds-badge>
                }
              }
            </div>
          </div>
          <soft-ds-button
            variant="ghost"
            size="md"
            mode="iconOnly"
            (click)="closePanel.emit()"
          >
            <soft-ds-icon *softSlot="'leading'" name="fa-regular fa-xmark" />
          </soft-ds-button>
        </div>

        <soft-ds-tabs
          variant="underline"
          size="sm"
          layout="scroll"
          [(value)]="activeTab"
          class="overflow-x-auto"
        >
          <soft-ds-tab [value]="0">Allgemein</soft-ds-tab>
          <soft-ds-tab [value]="1" [badge]="suppliers().length || null">
            Lieferanten
          </soft-ds-tab>
          <soft-ds-tab [value]="2" [badge]="unitConversions().length || null">
            Einheiten
          </soft-ds-tab>
          <soft-ds-tab [value]="3" [badge]="stockLevels().length || null">
            Lagerstand
          </soft-ds-tab>
          <soft-ds-tab [value]="4" [badge]="salesPrices().length || null">
            Preise
          </soft-ds-tab>
        </soft-ds-tabs>
      </div>
    </div>

    <!-- Body -->
    <div class="soft-product-detail__body">
      @if (productError()) {
        <!-- Main product load failed: replaces the body on every tab -->
        <soft-ds-empty-state
          class="soft-product-detail__error"
          variant="error"
          size="sm"
          title="Artikel konnte nicht geladen werden"
          description="Die Artikeldaten konnten nicht abgerufen werden. Bitte erneut versuchen."
          actionLabel="Erneut versuchen"
          (action)="retryLoad()"
        />
      } @else if (activeTab() === 0) {
        <!-- Image + Groups -->
        <div class="soft-product-detail__top">
          <div
            class="soft-product-detail__image"
            [class.soft-product-detail__image--photo]="!!currentImageSrc()"
          >
            @if (isLoadingProduct() && !currentImageSrc()) {
              <soft-ds-loading-skeleton width="100%" height="100%" radius="0" />
            } @else if (currentImageSrc(); as src) {
              <img [src]="src" [alt]="title()" class="soft-product-detail__image-img" />
            } @else {
              <div class="soft-product-detail__image-empty">
                <soft-ds-icon name="fa-regular fa-image" size="inherit" />
                <span class="soft-product-detail__image-empty-label">Kein Bild</span>
              </div>
            }

            @if (imageCount() > 1) {
              <button
                type="button"
                class="soft-product-detail__image-nav soft-product-detail__image-nav--prev"
                [disabled]="!canGoPrevImage()"
                (click)="onPrevImage()"
                title="Vorheriges Bild"
              >
                <i class="fa-regular fa-chevron-left"></i>
              </button>
              <button
                type="button"
                class="soft-product-detail__image-nav soft-product-detail__image-nav--next"
                [disabled]="!canGoNextImage()"
                (click)="onNextImage()"
                title="Nächstes Bild"
              >
                <i class="fa-regular fa-chevron-right"></i>
              </button>
              <div class="soft-product-detail__image-dots">
                @for (src of imageSrcs(); track $index; let i = $index) {
                  <button
                    type="button"
                    class="soft-product-detail__image-dot"
                    [class.is-active]="i === imageIndex()"
                    (click)="setImageIndex(i)"
                    [attr.aria-label]="'Bild ' + (i + 1)"
                  ></button>
                }
              </div>
            }
          </div>
          <div class="soft-product-detail__groups">
            @if (isLoadingProduct()) {
              <div class="soft-product-detail__group" *softRepeat="3">
                <soft-ds-loading-skeleton width="70px" height="10px" radius="4px" />
                <soft-ds-loading-skeleton width="130px" height="14px" radius="4px" />
              </div>
            } @else {
              <soft-ds-cell-grid [columns]="1">
                <soft-ds-cell label="Hauptgruppe" [value]="mainGroupName()" />
                <soft-ds-cell label="Untergruppe" [value]="subGroupName()" />
                <soft-ds-cell label="Produktgruppe" [value]="productGroupName()" />
              </soft-ds-cell-grid>
            }
          </div>
        </div>

        @if (isLoadingProduct()) {
          <!-- Loading skeleton: mirrors the field-grid section -->
          <section class="soft-product-detail__section">
            <soft-ds-loading-skeleton width="8rem" height="14px" radius="4px" />
            <div class="soft-product-detail__grid">
              <div class="soft-product-detail__field" *softRepeat="8">
                <soft-ds-loading-skeleton width="60%" height="10px" radius="4px" />
                <soft-ds-loading-skeleton width="80%" height="14px" radius="4px" />
              </div>
            </div>
          </section>
        } @else {
          <!-- Artikeldaten -->
          <section class="soft-product-detail__section">
            <soft-ds-cell-grid title="Artikeldaten">
              <soft-ds-cell label="Fibugruppe Verkauf" [value]="accountingSalesName()" />
              <soft-ds-cell
                label="Fibugruppe Einkauf"
                [value]="accountingPurchaseName()"
              />
              <soft-ds-cell label="Kzgelistet" [value]="listedFlag()" />
              <soft-ds-cell label="Artikelart" [value]="productType()" />
              <soft-ds-cell
                label="Pflanzenschutz-Registernummer"
                [value]="plantProtectionNumber()"
              />
              <soft-ds-cell label="Lager" [value]="storageManaged()" />
              <soft-ds-cell label="Datum der Einlistung" [value]="listedOn()" />
              <soft-ds-cell label="Datum der Auslistung" [value]="discontinuedOn()" />
              <soft-ds-cell label="Löschdatum" [value]="deletedOn()" />
              <soft-ds-cell label="Artikelnr." [value]="articleNumber()" />
              <soft-ds-cell label="Ext. Schlüssel" [value]="externalKey()" />
              <soft-ds-cell label="Ext. Schlüssel 2" [value]="externalKey2()" />
              <soft-ds-cell label="Unter-Untergruppe" [value]="subSubGroupName()" />
              <soft-ds-cell label="Steuer" [value]="taxName()" />
              <soft-ds-cell label="Konditionsgruppe" [value]="termGroupName()" />
              <soft-ds-cell label="Status" [value]="statusName()" />
              <soft-ds-cell label="Lagerstruktur" [value]="stockStructureName()" />
              <soft-ds-cell label="Marge" [value]="margin()" />
            </soft-ds-cell-grid>
          </section>

          <!-- Texte -->
          @if (textRows().length > 0) {
            <section class="soft-product-detail__section">
              <soft-ds-cell-grid title="Texte" [lines]="0">
                @for (t of textRows(); track t.label) {
                  <soft-ds-cell [label]="t.label" [value]="t.value" />
                }
              </soft-ds-cell-grid>
            </section>
          }

          <!-- Status je Verkaufsorganisation -->
          <section class="soft-product-detail__section">
            <p class="soft-product-detail__section-title">
              Status je Verkaufsorganisation
            </p>
            @if (isLoadingSalesOrganisationStatuses()) {
              <p class="soft-product-detail__placeholder">Lade Status…</p>
            } @else if (salesOrganisationStatuses().length === 0) {
              <p class="soft-product-detail__placeholder">Kein Status hinterlegt.</p>
            } @else {
              <div class="soft-product-detail__data-list">
                @for (row of salesOrganisationStatusRows(); track row.id) {
                  <div class="soft-product-detail__data-row">
                    <span class="soft-product-detail__data-key">{{ row.key }}</span>
                    <span class="soft-product-detail__data-title">{{ row.name }}</span>
                    <span class="soft-product-detail__data-value">{{ row.status }}</span>
                  </div>
                }
              </div>
            }
          </section>
        }
      } @else if (activeTab() === 1) {
        <!-- Lieferanten -->
        <section class="soft-product-detail__section soft-product-detail__suppliers">
          @if (isLoadingSuppliers()) {
            <ul class="soft-product-detail__supplier-list">
              <li class="soft-product-detail__supplier-card" *softRepeat="3">
                <div class="soft-product-detail__supplier-head">
                  <soft-ds-loading-skeleton width="40px" height="40px" radius="9999px" />
                  <div class="soft-product-detail__supplier-titles">
                    <soft-ds-loading-skeleton width="160px" height="14px" radius="4px" />
                    <soft-ds-loading-skeleton width="110px" height="10px" radius="4px" />
                  </div>
                </div>
              </li>
            </ul>
          } @else if (suppliers().length === 0) {
            <p class="soft-product-detail__placeholder">Keine Lieferanten hinterlegt.</p>
          } @else {
            <div class="soft-product-detail__supplier-header">
              <p class="soft-product-detail__section-title">
                {{ suppliers().length }} Lieferant{{
                  suppliers().length === 1 ? '' : 'en'
                }}
              </p>
            </div>
            <ul class="soft-product-detail__supplier-list">
              @for (s of suppliers(); track s.id) {
                <li
                  class="soft-product-detail__supplier-card"
                  [class.is-main]="s.id === mainSupplierId()"
                >
                  <div class="soft-product-detail__supplier-head">
                    <soft-ds-avatar size="md" [initials]="supplierInitials(s.supplier)" />
                    <div class="soft-product-detail__supplier-titles">
                      <p class="soft-product-detail__supplier-name">
                        {{ s.supplier?.name ?? '—' }}
                        @if (s.id === mainSupplierId()) {
                          <span class="soft-product-detail__supplier-badge">
                            ★ Bevorzugt
                          </span>
                        }
                      </p>
                      @if (s.supplierProductNumber) {
                        <p class="soft-product-detail__supplier-sub">
                          Lief.-Art.-Nr.: {{ s.supplierProductNumber }}
                        </p>
                      }
                    </div>
                  </div>
                  <dl class="soft-product-detail__supplier-grid">
                    <div>
                      <dt>Lieferzeit</dt>
                      <dd>
                        @if (s.deliveryDays > 0) {
                          {{ s.deliveryDays }} T
                        } @else {
                          —
                        }
                      </dd>
                    </div>
                    <div>
                      <dt>Mindestmenge</dt>
                      <dd>
                        @if (s.minQuantity > 0) {
                          {{ s.minQuantity }} {{ s.unit?.key ?? '' }}
                        } @else {
                          —
                        }
                      </dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>{{ s.supplierStatus?.name ?? '—' }}</dd>
                    </div>
                  </dl>
                </li>
              }
            </ul>
          }
        </section>
      } @else if (activeTab() === 2) {
        <section class="soft-product-detail__section">
          <p class="soft-product-detail__section-title">Einheiten (Stammdaten)</p>
          <div class="soft-product-detail__data-list">
            @for (u of unitRows(); track u.label) {
              <div
                class="soft-product-detail__data-row soft-product-detail__data-row--pair"
              >
                <span class="soft-product-detail__data-title">{{ u.label }}</span>
                <span class="soft-product-detail__data-value">{{ u.value }}</span>
              </div>
            }
          </div>
        </section>
        <section class="soft-product-detail__section soft-product-detail__units">
          @if (isLoadingUnitConversions()) {
            <ul class="soft-product-detail__unit-list">
              <li class="soft-product-detail__unit-card" *softRepeat="3">
                <soft-ds-loading-skeleton width="180px" height="14px" radius="4px" />
                <soft-ds-loading-skeleton width="120px" height="10px" radius="4px" />
              </li>
            </ul>
          } @else if (unitConversions().length === 0) {
            <p class="soft-product-detail__placeholder">Keine Einheiten hinterlegt.</p>
          } @else {
            <div class="soft-product-detail__supplier-header">
              <p class="soft-product-detail__section-title">
                {{ unitConversions().length }} Einheit{{
                  unitConversions().length === 1 ? '' : 'en'
                }}
              </p>
            </div>
            <ul class="soft-product-detail__unit-list">
              @for (u of unitConversions(); track u.id) {
                <li
                  class="soft-product-detail__unit-card"
                  [class.is-inactive]="!u.active"
                >
                  <div class="soft-product-detail__unit-head">
                    <p class="soft-product-detail__unit-name">
                      {{ u.unit?.name ?? u.unit?.key ?? '—' }}
                      @if (u.unit?.key) {
                        <span class="soft-product-detail__unit-key">
                          {{ u.unit?.key }}
                        </span>
                      }
                      @if (unitTypeLabel(u.type); as typeLabel) {
                        @if (typeLabel) {
                          <soft-ds-badge size="sm" variant="info">
                            {{ typeLabel }}
                          </soft-ds-badge>
                        }
                      }
                      @if (!u.active) {
                        <soft-ds-badge size="sm" variant="secondary">
                          Inaktiv
                        </soft-ds-badge>
                      }
                    </p>
                  </div>
                  <dl class="soft-product-detail__supplier-grid">
                    <div>
                      <dt>Faktor</dt>
                      <dd>{{ u.factor }}</dd>
                    </div>
                    <div>
                      <dt>EAN</dt>
                      <dd>{{ u.ean ?? '—' }}</dd>
                    </div>
                    <div>
                      <dt>Weitere EANs</dt>
                      <dd>
                        @if (u.additionalEanList?.length) {
                          {{ u.additionalEanList.join(', ') }}
                        } @else {
                          —
                        }
                      </dd>
                    </div>
                  </dl>
                  @if (u.ean?.trim() || u.additionalEanList?.length) {
                    <div class="soft-product-detail__unit-barcodes">
                      @if (u.ean?.trim(); as ean) {
                        <soft-barcode
                          [data]="ean"
                          [labelType]="ean.length <= 8 ? 'ean8' : 'ean13'"
                          size="sm"
                        />
                      }
                      @for (ean of u.additionalEanList; track ean) {
                        @if (ean?.trim()) {
                          <soft-barcode
                            [data]="ean"
                            [labelType]="ean.length <= 8 ? 'ean8' : 'ean13'"
                            size="sm"
                          />
                        }
                      }
                    </div>
                  }
                </li>
              }
            </ul>
          }
        </section>
      } @else if (activeTab() === 3) {
        <!-- Lagerstand: one row per warehouse the article moves in -->
        <section class="soft-product-detail__section">
          <div class="soft-product-detail__section-row">
            <p class="soft-product-detail__section-title">Lagerstand je Filiale</p>
            @if (!isLoadingStockLevels() && stockLevels().length > 0) {
              <p class="soft-product-detail__section-title">
                {{ stockLevelTotal() | number: '1.0-3' }}
                {{ storageUnitName() }}
              </p>
            }
          </div>
          @if (!isLoadingStockLevels() && dispositionTotal() !== null) {
            <div class="soft-product-detail__section-row">
              <span class="soft-product-detail__data-sub">Dispostand</span>
              <span class="soft-product-detail__data-sub">
                {{ dispositionTotal() | number: '1.0-3' }}
                {{ storageUnitName() }}
              </span>
            </div>
          }

          @if (isLoadingStockLevels()) {
            <div class="soft-product-detail__data-list">
              <div
                class="soft-product-detail__data-row soft-product-detail__data-row--pair"
                *softRepeat="3"
              >
                <soft-ds-loading-skeleton width="120px" height="12px" radius="4px" />
                <soft-ds-loading-skeleton width="60px" height="12px" radius="4px" />
              </div>
            </div>
          } @else if (stockLevels().length === 0) {
            <p class="soft-product-detail__placeholder">
              Keine Lagerbewegungen zu diesem Artikel.
            </p>
          } @else {
            <div class="soft-product-detail__data-list">
              @for (row of stockLevelRows(); track row.id) {
                <div class="soft-product-detail__data-row">
                  <span class="soft-product-detail__data-key">{{ row.key }}</span>
                  <span class="soft-product-detail__data-title">{{ row.name }}</span>
                  <span
                    class="soft-product-detail__data-value is-numeric"
                    [class.is-negative]="row.amount < 0"
                  >
                    {{ row.amount | number: '1.0-3' }}
                  </span>
                  @if (storageUnitName()) {
                    <span class="soft-product-detail__data-unit">
                      {{ storageUnitName() }}
                    </span>
                  }
                </div>
                @if (row.disposition !== null) {
                  <div
                    class="soft-product-detail__data-row soft-product-detail__data-row--sub"
                    [attr.data-testid]="'product-stock-dispo-' + row.key"
                  >
                    <span class="soft-product-detail__data-key"></span>
                    <span class="soft-product-detail__data-title">Dispostand</span>
                    <span
                      class="soft-product-detail__data-value is-numeric"
                      [class.is-muted]="row.disposition >= 0"
                      [class.is-negative]="row.disposition < 0"
                    >
                      {{ row.disposition | number: '1.0-3' }}
                    </span>
                    @if (storageUnitName()) {
                      <span class="soft-product-detail__data-unit">
                        {{ storageUnitName() }}
                      </span>
                    }
                  </div>
                }
              }
            </div>
          }
        </section>
      } @else if (activeTab() === 4) {
        <section class="soft-product-detail__section">
          <div class="soft-product-detail__section-row">
            <p class="soft-product-detail__section-title">Verkaufspreise</p>
            @if (salesPriceOrg() || salesPriceUnit()) {
              <span class="soft-product-detail__data-sub">
                {{ salesPriceOrg() }}
                @if (salesPriceUnit()) {
                  · je {{ salesPriceUnit() }}
                }
              </span>
            }
          </div>
          @if (isLoadingSalesPrices()) {
            <p class="soft-product-detail__placeholder">Lade Verkaufspreise…</p>
          } @else if (salesPrices().length === 0) {
            <p class="soft-product-detail__placeholder">
              Keine Verkaufspreise hinterlegt.
            </p>
          } @else {
            @if (salesPriceGroups().length > 0) {
              <div class="soft-product-detail__data-list">
                @for (group of salesPriceGroups(); track group.id) {
                  <div class="soft-product-detail__data-group">
                    <div class="soft-product-detail__data-group-head">
                      <span>{{ group.validity || 'Ohne Gültigkeitsangabe' }}</span>
                      @if (!salesPriceOrg()) {
                        <span class="soft-product-detail__data-sub">
                          @if (group.orgKey) {
                            {{ group.orgKey }} ·
                          }
                          {{ group.org }}
                        </span>
                      }
                      @if (group.ended) {
                        <soft-ds-badge size="sm" variant="warning">
                          Abgelaufen
                        </soft-ds-badge>
                      }
                    </div>
                  </div>
                  @for (row of group.rows; track row.id) {
                    <div
                      class="soft-product-detail__data-row soft-product-detail__data-row--price"
                    >
                      <span class="soft-product-detail__data-label">
                        <span class="soft-product-detail__data-label-head">
                          <span class="soft-product-detail__data-title">
                            @if (row.fromAmount > 0) {
                              ab {{ row.fromAmount | number: '1.0-3' }}
                            } @else {
                              Preisstufe {{ row.level || '—' }}
                            }
                          </span>
                          @if (row.taxIncluded) {
                            <soft-ds-badge size="sm" variant="secondary">
                              inkl. USt
                            </soft-ds-badge>
                          }
                        </span>
                        @if (row.per || row.additionalTerms) {
                          <span class="soft-product-detail__data-sub">
                            @if (row.per) {
                              je {{ row.per | number: '1.0-3' }}
                              {{ group.unit }}
                            }
                            @if (row.per && row.additionalTerms) {
                              ·
                            }
                            {{ row.additionalTerms }}
                          </span>
                        }
                      </span>
                      <span class="soft-product-detail__data-value is-numeric">
                        {{ row.netPrice | number: priceFormat() }}
                      </span>
                      <span class="soft-product-detail__data-unit">netto</span>
                      @if (row.grossPrice !== null) {
                        <span class="soft-product-detail__data-value is-numeric is-muted">
                          {{ row.grossPrice | number: priceFormat() }}
                        </span>
                        <span class="soft-product-detail__data-unit">brutto</span>
                      }
                    </div>
                  }
                }
              </div>

              @if (basePriceInfo(); as base) {
                <div class="soft-product-detail__data-list">
                  <div
                    class="soft-product-detail__data-row soft-product-detail__data-row--pair"
                  >
                    <span class="soft-product-detail__data-title">Inhalt</span>
                    <span class="soft-product-detail__data-value">
                      {{ base.netContent | number: '1.0-3' }} {{ base.contentUnit }}
                    </span>
                  </div>
                  <div
                    class="soft-product-detail__data-row soft-product-detail__data-row--pair"
                  >
                    <span class="soft-product-detail__data-title">
                      Faktor ({{ base.declarationUnit }})
                    </span>
                    <span class="soft-product-detail__data-value is-numeric">
                      {{ base.factor | number: '1.0-3' }}
                    </span>
                  </div>
                  @if (base.consumerDeclaration) {
                    <div
                      class="soft-product-detail__data-row soft-product-detail__data-row--pair"
                    >
                      <span class="soft-product-detail__data-title">
                        Verbraucherangabe
                      </span>
                      <span class="soft-product-detail__data-value">
                        {{ base.consumerDeclaration }}
                      </span>
                    </div>
                  }
                </div>
              }
            } @else {
              <p class="soft-product-detail__placeholder">
                Keine aktuell gültigen Preise.
              </p>
            }
            @if (historicalPriceCount() > 0) {
              <soft-ds-button
                variant="ghost"
                size="sm"
                data-testid="product-price-history-toggle"
                (click)="togglePriceHistory()"
              >
                {{
                  showPriceHistory()
                    ? 'Nur aktuelle Preise anzeigen'
                    : 'Historische Preise anzeigen (' + historicalPriceCount() + ')'
                }}
              </soft-ds-button>
            }
          }
        </section>
      }
    </div>

    <!-- Pager -->
    @if (items().length > 0) {
      <div class="soft-product-detail__pager">
        <button
          type="button"
          class="soft-product-detail__nav"
          [disabled]="!canGoPrev()"
          (click)="onPrev()"
          title="Vorheriger Artikel"
        >
          <i class="fa-regular fa-arrow-left"></i>
        </button>
        <div class="soft-product-detail__pager-text">
          <p>{{ pagerLabel() }}</p>
          <p class="soft-product-detail__pager-hint">
            Pfeiltasten oder Symbole zum Weiterblättern nutzen
          </p>
        </div>
        <button
          type="button"
          class="soft-product-detail__nav"
          [disabled]="!canGoNext()"
          (click)="onNext()"
          title="Nächster Artikel"
        >
          <i class="fa-regular fa-arrow-right"></i>
        </button>
      </div>
    }
  </div>
}
